'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trophy, Flame, Crown, Medal, Target, ChevronUp, ChevronDown, Minus, RefreshCw } from 'lucide-react';
import { quizService } from '@/lib/services/quizService';

// ─── Types ────────────────────────────────────────────────────────────────────
interface QuizSession {
  date: string;
  score: number;
  total: number;
  xp: number;
  difficulty: string;
}

interface LeaderboardEntry {
  rank: number;
  name: string;
  xp: number;
  streak: number;
  accuracy: number;
  badge: string;
  isYou?: boolean;
  trend: 'up' | 'down' | 'same';
}

// ─── Storage Keys ─────────────────────────────────────────────────────────────
const STORAGE_KEY_SESSIONS = 'ibrain_quiz_sessions';
const STORAGE_KEY_STREAK = 'ibrain_quiz_streak';
const STORAGE_KEY_LAST_DATE = 'ibrain_quiz_last_date';
const STORAGE_KEY_TOTAL_XP = 'ibrain_quiz_total_xp';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

function getYesterdayStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

function calcXP(score: number, total: number, difficulty: string): number {
  const base = score * 10;
  const bonus = difficulty === 'hard' ? 1.5 : difficulty === 'medium' ? 1.2 : 1.0;
  const perfect = score === total ? 25 : 0;
  return Math.round(base * bonus + perfect);
}

function getBadge(xp: number): { label: string; emoji: string; color: string } {
  if (xp >= 1000) return { label: 'Neural Master', emoji: '🧠', color: '#a855f7' };
  if (xp >= 500) return { label: 'Synapse Pro', emoji: '⚡', color: '#F97316' };
  if (xp >= 200) return { label: 'Cortex Cadet', emoji: '🎯', color: '#22d3ee' };
  if (xp >= 50) return { label: 'Neuron Rookie', emoji: '🌱', color: '#22c55e' };
  return { label: 'Brain Beginner', emoji: '🔬', color: '#8BAFD4' };
}

function getStreakEmoji(streak: number): string {
  if (streak >= 14) return '🔥🔥';
  if (streak >= 7) return '🔥';
  if (streak >= 3) return '⚡';
  if (streak >= 1) return '✨';
  return '💤';
}

// ─── XP Progress Bar ──────────────────────────────────────────────────────────
function XPProgressBar({ xp }: { xp: number }) {
  const thresholds = [0, 50, 200, 500, 1000, 2000];
  const currentTier = thresholds.findIndex(t => xp < t) - 1;
  const tierIdx = Math.max(0, Math.min(currentTier, thresholds.length - 2));
  const tierMin = thresholds[tierIdx];
  const tierMax = thresholds[tierIdx + 1] ?? thresholds[thresholds.length - 1];
  const pct = Math.min(((xp - tierMin) / (tierMax - tierMin)) * 100, 100);
  const badge = getBadge(xp);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-bold" style={{ color: badge.color }}>
          {badge.emoji} {badge.label}
        </span>
        <span className="text-[10px] text-[#4A7AB5]">{xp} / {tierMax} XP</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(30,58,95,0.4)' }}>
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${badge.color}99, ${badge.color})`,
            boxShadow: `0 0 8px ${badge.color}60`,
          }}
        />
      </div>
      <p className="text-[9px] text-[#4A7AB5] mt-1">{tierMax - xp} XP to next tier</p>
    </div>
  );
}

// ─── Streak Calendar (last 7 days) ───────────────────────────────────────────
function StreakCalendar({ sessions }: { sessions: QuizSession[] }) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const dateStr = d.toISOString().split('T')[0];
    const played = sessions.some(s => s.date === dateStr);
    const dayLabel = d.toLocaleDateString('en', { weekday: 'short' }).slice(0, 1);
    return { dateStr, played, dayLabel, isToday: dateStr === getTodayStr() };
  });

  return (
    <div className="flex items-end justify-between gap-1">
      {days.map((day) => (
        <div key={day.dateStr} className="flex flex-col items-center gap-1 flex-1">
          <div
            className="w-full rounded-md transition-all duration-300"
            style={{
              height: day.played ? '28px' : '16px',
              background: day.played
                ? 'linear-gradient(180deg, #F97316, #FF6B00)'
                : day.isToday
                  ? 'rgba(249,115,22,0.15)'
                  : 'rgba(30,58,95,0.3)',
              border: day.isToday
                ? '1px solid rgba(249,115,22,0.4)'
                : '1px solid transparent',
              boxShadow: day.played ? '0 0 8px rgba(249,115,22,0.4)' : 'none',
            }}
          />
          <span
            className="text-[8px] font-bold"
            style={{ color: day.isToday ? '#F97316' : '#4A7AB5' }}
          >
            {day.dayLabel}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Leaderboard Row ──────────────────────────────────────────────────────────
function LeaderboardRow({ entry }: { entry: LeaderboardEntry }) {
  const rankColors: Record<number, string> = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
  const rankColor = rankColors[entry.rank] ?? '#4A7AB5';
  const RankIcon = entry.rank === 1 ? Crown : entry.rank <= 3 ? Medal : null;

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200"
      style={{
        background: entry.isYou
          ? 'linear-gradient(90deg, rgba(249,115,22,0.12), rgba(249,115,22,0.06))'
          : 'rgba(30,58,95,0.08)',
        border: entry.isYou
          ? '1px solid rgba(249,115,22,0.35)'
          : '1px solid rgba(30,58,95,0.2)',
        boxShadow: entry.isYou ? '0 0 12px rgba(249,115,22,0.08)' : 'none',
      }}
    >
      {/* Rank */}
      <div className="w-7 flex items-center justify-center flex-shrink-0">
        {RankIcon ? (
          <RankIcon className="w-4 h-4" style={{ color: rankColor }} />
        ) : (
          <span className="text-[11px] font-black" style={{ color: rankColor }}>#{entry.rank}</span>
        )}
      </div>

      {/* Badge emoji */}
      <span className="text-base flex-shrink-0">{entry.badge}</span>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className="text-[12px] font-black truncate"
            style={{ color: entry.isYou ? '#F97316' : '#e2e8f0' }}
          >
            {entry.name}
          </span>
          {entry.isYou && (
            <span
              className="text-[8px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{ background: 'rgba(249,115,22,0.2)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)' }}
            >
              YOU
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-[#4A7AB5]">🔥 {entry.streak}d</span>
          <span className="text-[9px] text-[#4A7AB5]">🎯 {entry.accuracy}%</span>
        </div>
      </div>

      {/* XP + trend */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-[12px] font-black text-yellow-400">{entry.xp.toLocaleString()}</span>
        <span className="text-[8px] text-[#4A7AB5]">XP</span>
        {entry.trend === 'up' && <ChevronUp className="w-3 h-3 text-green-400" />}
        {entry.trend === 'down' && <ChevronDown className="w-3 h-3 text-red-400" />}
        {entry.trend === 'same' && <Minus className="w-3 h-3 text-[#4A7AB5]" />}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LeaderboardStreakPanel() {
  const [sessions, setSessions] = useState<QuizSession[]>([]);
  const [streak, setStreak] = useState(0);
  const [totalXP, setTotalXP] = useState(0);
  const [tab, setTab] = useState<'leaderboard' | 'stats'>('leaderboard');
  const [showAll, setShowAll] = useState(false);
  const [supabaseLeaderboard, setSupabaseLeaderboard] = useState<Array<{
    playerName: string; totalXp: number; streak: number; avgAccuracy: number;
  }>>([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  // Load from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SESSIONS);
      const storedSessions: QuizSession[] = raw ? JSON.parse(raw) : [];
      setSessions(storedSessions);

      const storedStreak = parseInt(localStorage.getItem(STORAGE_KEY_STREAK) ?? '0', 10);
      const lastDate = localStorage.getItem(STORAGE_KEY_LAST_DATE) ?? '';
      const storedXP = parseInt(localStorage.getItem(STORAGE_KEY_TOTAL_XP) ?? '0', 10);

      const today = getTodayStr();
      const yesterday = getYesterdayStr();
      if (lastDate !== today && lastDate !== yesterday) {
        setStreak(0);
        localStorage.setItem(STORAGE_KEY_STREAK, '0');
      } else {
        setStreak(storedStreak);
      }
      setTotalXP(storedXP);
    } catch {
      // ignore storage errors
    }
  }, []);

  // Load Supabase leaderboard
  const loadLeaderboard = useCallback(async () => {
    setLoadingLeaderboard(true);
    try {
      const data = await quizService.fetchLeaderboard(20);
      setSupabaseLeaderboard(data.map(e => ({
        playerName: e.playerName,
        totalXp: e.totalXp,
        streak: e.streak,
        avgAccuracy: Math.round(e.avgAccuracy),
      })));
    } catch {
      // fallback to mock data
    } finally {
      setLoadingLeaderboard(false);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  // Listen for quiz completion events dispatched by BrainHealthQuiz
  useEffect(() => {
    const handler = (e: CustomEvent<{ score: number; total: number; difficulty: string }>) => {
      const { score, total, difficulty } = e.detail;
      const xp = calcXP(score, total, difficulty);
      const today = getTodayStr();

      setSessions(prev => {
        const updated = [...prev, { date: today, score, total, xp, difficulty }];
        try { localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updated.slice(-90))); } catch {}
        return updated;
      });

      setTotalXP(prev => {
        let next = prev + xp;
        try { localStorage.setItem(STORAGE_KEY_TOTAL_XP, String(next)); } catch {}

        // Upsert to Supabase leaderboard
        const currentStreak = parseInt(localStorage.getItem(STORAGE_KEY_STREAK) ?? '0', 10);
        quizService.upsertLeaderboard({
          playerName: 'anonymous',
          xpGained: xp,
          score,
          total,
          streak: currentStreak + 1,
        }).then(() => loadLeaderboard()).catch(() => {/* non-critical */});

        return next;
      });

      setStreak(prev => {
        const lastDate = localStorage.getItem(STORAGE_KEY_LAST_DATE) ?? '';
        const yesterday = getYesterdayStr();
        let next = prev;
        if (lastDate === today) {
          next = prev;
        } else if (lastDate === yesterday || lastDate === '') {
          next = prev + 1;
        } else {
          next = 1;
        }
        try {
          localStorage.setItem(STORAGE_KEY_STREAK, String(next));
          localStorage.setItem(STORAGE_KEY_LAST_DATE, today);
        } catch {}
        return next;
      });
    };

    window.addEventListener('quiz:complete', handler as EventListener);
    return () => window.removeEventListener('quiz:complete', handler as EventListener);
  }, [loadLeaderboard]);

  // Build leaderboard: merge Supabase data with local "You" entry
  const leaderboard: LeaderboardEntry[] = useMemo(() => {
    const userAccuracy = sessions.length > 0
      ? Math.round((sessions.reduce((a, s) => a + s.score, 0) / sessions.reduce((a, s) => a + s.total, 0)) * 100)
      : 0;
    const badge = getBadge(totalXP);

    const userEntry = {
      name: 'You',
      xp: totalXP,
      streak,
      accuracy: userAccuracy,
      badge: badge.emoji,
      isYou: true,
      trend: 'same' as const,
    };

    // Use Supabase leaderboard if available, otherwise use mock peers
    const MOCK_PEERS = [
      { name: 'NeuroNova', xp: 1240, streak: 18, accuracy: 91, badge: '🧠', isYou: false, trend: 'up' as const },
      { name: 'SynapseX', xp: 980, streak: 12, accuracy: 87, badge: '⚡', isYou: false, trend: 'same' as const },
      { name: 'CortexKing', xp: 760, streak: 9, accuracy: 83, badge: '🎯', isYou: false, trend: 'down' as const },
      { name: 'BetaWave', xp: 540, streak: 6, accuracy: 79, badge: '🎯', isYou: false, trend: 'up' as const },
      { name: 'AlphaRider', xp: 310, streak: 4, accuracy: 74, badge: '🌱', isYou: false, trend: 'same' as const },
      { name: 'DeltaDiver', xp: 180, streak: 2, accuracy: 68, badge: '🌱', isYou: false, trend: 'down' as const },
    ];

    const peers = supabaseLeaderboard.length > 0
      ? supabaseLeaderboard
          .filter(e => e.playerName !== 'anonymous')
          .map(e => ({
            name: e.playerName,
            xp: e.totalXp,
            streak: e.streak,
            accuracy: e.avgAccuracy,
            badge: getBadge(e.totalXp).emoji,
            isYou: false,
            trend: 'same' as const,
          }))
      : MOCK_PEERS;

    const allEntries = [...peers, userEntry]
      .sort((a, b) => b.xp - a.xp)
      .map((e, i) => ({ ...e, rank: i + 1 }));

    return allEntries;
  }, [totalXP, streak, sessions, supabaseLeaderboard]);

  const userRank = leaderboard.find(e => e.isYou)?.rank ?? leaderboard.length;
  const displayedLeaderboard = showAll ? leaderboard : leaderboard.slice(0, 5);

  const todayPlayed = sessions.some(s => s.date === getTodayStr());
  const weekSessions = sessions.filter(s => {
    const d = new Date(s.date);
    const now = new Date();
    const diff = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
    return diff <= 7;
  });
  const weekXP = weekSessions.reduce((a, s) => a + s.xp, 0);
  const avgAccuracy = sessions.length > 0
    ? Math.round((sessions.reduce((a, s) => a + s.score, 0) / sessions.reduce((a, s) => a + s.total, 0)) * 100)
    : 0;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, rgba(11,22,40,0.98) 0%, rgba(6,13,26,1) 100%)',
        border: '1px solid rgba(249,115,22,0.25)',
        boxShadow: '0 0 40px rgba(249,115,22,0.07), 0 8px 32px rgba(0,0,0,0.5)',
      }}
    >
      {/* Top accent */}
      <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #F97316 30%, #FFD700 50%, #F97316 70%, transparent)' }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)' }}
            >
              <Trophy className="w-4.5 h-4.5 text-[#F97316]" />
            </div>
            <div>
              <h3 className="text-[14px] font-black text-white leading-tight">Leaderboard & Streaks</h3>
              <p className="text-[10px] text-[#4A7AB5]">Daily Quiz Performance</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Refresh leaderboard */}
            <button
              onClick={loadLeaderboard}
              disabled={loadingLeaderboard}
              className="p-1.5 rounded-lg text-[#4A7AB5] hover:text-zinc-200 transition-colors"
              style={{ background: 'rgba(30,58,95,0.2)', border: '1px solid rgba(30,58,95,0.3)' }}
              title="Refresh leaderboard"
            >
              <RefreshCw className={`w-3 h-3 ${loadingLeaderboard ? 'animate-spin' : ''}`} />
            </button>

            {/* Streak badge */}
            <div
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl"
              style={{
                background: streak > 0 ? 'rgba(249,115,22,0.12)' : 'rgba(30,58,95,0.2)',
                border: streak > 0 ? '1px solid rgba(249,115,22,0.35)' : '1px solid rgba(30,58,95,0.3)',
              }}
            >
              <Flame className="w-4 h-4" style={{ color: streak > 0 ? '#F97316' : '#4A7AB5' }} />
              <span className="text-[13px] font-black" style={{ color: streak > 0 ? '#F97316' : '#4A7AB5' }}>{streak}</span>
              <span className="text-[9px] text-[#4A7AB5]">day streak</span>
            </div>
          </div>
        </div>

        {/* Today's status */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl mb-4"
          style={{
            background: todayPlayed ? 'rgba(34,211,238,0.08)' : 'rgba(249,115,22,0.06)',
            border: todayPlayed ? '1px solid rgba(34,211,238,0.25)' : '1px solid rgba(249,115,22,0.2)',
          }}
        >
          <span className="text-base">{todayPlayed ? '✅' : '🎯'}</span>
          <span className="text-[11px] font-bold" style={{ color: todayPlayed ? '#22d3ee' : '#F97316' }}>
            {todayPlayed ? "Today's quiz completed!" : 'Complete today\'s quiz to keep your streak!'}
          </span>
          {todayPlayed && (
            <span className="ml-auto text-[10px] text-[#4A7AB5]">
              +{sessions.filter(s => s.date === getTodayStr()).reduce((a, s) => a + s.xp, 0)} XP today
            </span>
          )}
        </div>

        {/* XP Progress */}
        <div
          className="rounded-xl p-3 mb-4"
          style={{ background: 'rgba(6,13,26,0.6)', border: '1px solid rgba(30,58,95,0.3)' }}
        >
          <XPProgressBar xp={totalXP} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background: 'rgba(6,13,26,0.6)', border: '1px solid rgba(30,58,95,0.3)' }}>
          {(['leaderboard', 'stats'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="flex-1 py-1.5 rounded-lg text-[11px] font-bold capitalize transition-all duration-200"
              style={{
                background: tab === t ? 'rgba(249,115,22,0.15)' : 'transparent',
                color: tab === t ? '#F97316' : '#4A7AB5',
                border: tab === t ? '1px solid rgba(249,115,22,0.3)' : '1px solid transparent',
              }}
            >
              {t === 'leaderboard' ? '🏆 Leaderboard' : '📊 My Stats'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'leaderboard' && (
          <div>
            {/* Your rank callout */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-bold">
                {supabaseLeaderboard.length > 0 ? 'Live Global Ranking' : 'Local Ranking'}
              </span>
              <span
                className="text-[10px] font-black px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316', border: '1px solid rgba(249,115,22,0.25)' }}
              >
                You: #{userRank}
              </span>
            </div>

            <div className="space-y-1.5">
              {displayedLeaderboard.map(entry => (
                <LeaderboardRow key={entry.name} entry={entry} />
              ))}
            </div>

            {leaderboard.length > 5 && (
              <button
                onClick={() => setShowAll(v => !v)}
                className="w-full mt-2 py-2 rounded-xl text-[10px] font-bold text-[#4A7AB5] transition-all duration-200 flex items-center justify-center gap-1"
                style={{ background: 'rgba(30,58,95,0.1)', border: '1px solid rgba(30,58,95,0.25)' }}
              >
                {showAll ? 'Show Less' : `Show All ${leaderboard.length} Players`}
                {showAll ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
          </div>
        )}

        {tab === 'stats' && (
          <div className="space-y-4">
            {/* 7-day streak calendar */}
            <div>
              <p className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
                <Flame className="w-2.5 h-2.5" /> Last 7 Days
              </p>
              <div
                className="rounded-xl p-3"
                style={{ background: 'rgba(6,13,26,0.6)', border: '1px solid rgba(30,58,95,0.3)' }}
              >
                <StreakCalendar sessions={sessions} />
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Total XP', value: totalXP.toLocaleString(), unit: 'XP', color: '#FFD700', icon: '⭐' },
                { label: 'Best Streak', value: String(streak), unit: 'days', color: '#F97316', icon: '🔥' },
                { label: 'Avg Accuracy', value: `${avgAccuracy}`, unit: '%', color: '#22d3ee', icon: '🎯' },
                { label: 'This Week', value: String(weekXP), unit: 'XP', color: '#a855f7', icon: '📅' },
              ].map(stat => (
                <div
                  key={stat.label}
                  className="rounded-xl p-3 flex flex-col"
                  style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}25` }}
                >
                  <span className="text-base mb-1">{stat.icon}</span>
                  <span className="text-[20px] font-black leading-none" style={{ color: stat.color }}>{stat.value}</span>
                  <span className="text-[9px] text-[#4A7AB5] mt-0.5">{stat.unit}</span>
                  <span className="text-[9px] text-[#4A7AB5] uppercase tracking-widest mt-1 font-bold">{stat.label}</span>
                </div>
              ))}
            </div>

            {/* Recent sessions */}
            {sessions.length > 0 && (
              <div>
                <p className="text-[10px] text-[#4A7AB5] uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
                  <Target className="w-2.5 h-2.5" /> Recent Sessions
                </p>
                <div className="space-y-1.5">
                  {sessions.slice(-5).reverse().map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl"
                      style={{ background: 'rgba(30,58,95,0.08)', border: '1px solid rgba(30,58,95,0.2)' }}
                    >
                      <span className="text-[10px] text-[#4A7AB5] w-20 flex-shrink-0">{s.date}</span>
                      <span className="text-[11px] font-bold text-white flex-1">{s.score}/{s.total} correct</span>
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{
                          color: s.difficulty === 'hard' ? '#ef4444' : s.difficulty === 'medium' ? '#F97316' : '#22d3ee',
                          background: s.difficulty === 'hard' ? 'rgba(239,68,68,0.1)' : s.difficulty === 'medium' ? 'rgba(249,115,22,0.1)' : 'rgba(34,211,238,0.1)',
                        }}
                      >
                        {s.difficulty}
                      </span>
                      <span className="text-[10px] font-black text-yellow-400">+{s.xp} XP</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sessions.length === 0 && (
              <div className="text-center py-6">
                <span className="text-3xl block mb-2">🧠</span>
                <p className="text-[12px] text-[#4A7AB5]">No quiz sessions yet.</p>
                <p className="text-[10px] text-[#4A7AB5] mt-1">Complete a Brain Health Quiz to start earning XP!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Public helper to dispatch quiz completion event ──────────────────────────
export function dispatchQuizComplete(score: number, total: number, difficulty: string) {
  window.dispatchEvent(new CustomEvent('quiz:complete', { detail: { score, total, difficulty } }));
}
